import { useContext, useMemo } from 'react';
import { CsInterfaceContext } from '../index';
import { getEnvironmentDetector, EnvironmentConfig, ENVIRONMENT_CONFIGS } from '../utils/environmentDetector';

export function useEnvironment() {
  const csInterface = useContext(CsInterfaceContext);
  
  const environmentDetector = useMemo(() => {
    // If no csInterface (running in browser), return a mock detector with local config
    if (!csInterface) {
      const localConfig = ENVIRONMENT_CONFIGS['com.Blingsio.blingsExt.local'];
      return {
        getCurrentConfig: () => localConfig,
        getEnvironment: (): 'master' | 'dev' | 'local' => 'local',
        getServerId: () => localConfig.serverId,
        getExtensionId: () => localConfig.extensionId,
        getBundleId: () => localConfig.bundleId,
        getPort: () => localConfig.port,
        getDebugPorts: () => localConfig.debugPorts,
        getMenuName: () => localConfig.menuName,
        isDevelopment: (): boolean => true,
        isProduction: (): boolean => false
      };
    }
    return getEnvironmentDetector(csInterface);
  }, [csInterface]);

  const config = useMemo(() => {
    return environmentDetector.getCurrentConfig();
  }, [environmentDetector]);

  return {
    // Environment info
    environment: config.environment,
    isProduction: config.environment === 'master',
    isDevelopment: config.environment === 'dev' || config.environment === 'local',
    isLocal: config.environment === 'local',
    isDev: config.environment === 'dev',
    isMaster: config.environment === 'master',
    
    // Extension configuration
    bundleId: config.bundleId,
    extensionId: config.extensionId,
    serverId: config.serverId,
    menuName: config.menuName,
    port: config.port,
    debugPorts: config.debugPorts,
    
    // Full configuration object
    config,
    
    // Environment detector instance
    detector: environmentDetector
  };
}

export type UseEnvironmentReturn = ReturnType<typeof useEnvironment>;
